import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import {
  attachIntentionalMapInteraction,
  attachMapResetControl,
  createTileLayer,
  fitMapBoundsAsHome,
} from '../../../../utils/mapUtils';
import {
  buildSimulatorMapCellTooltipHtml,
  getCellStyle,
} from '../utils/simulatorMapCellStyles';

const SIMULATOR_MAP_ELEMENT_ID = 'simulator-map';
const SIMULATOR_WIND_MAP_ELEMENT_ID = 'simulator-map-wind';

function initSimulatorMap(elementId) {
  const el = document.getElementById(elementId);
  if (!el) return null;

  const map = L.map(el, {
    zoomControl: true,
    attributionControl: false,
    scrollWheelZoom: false,
    dragging: false,
    touchZoom: false,
  });
  createTileLayer('LIGHT_SUBTLE').addTo(map);
  map.setView([20, 0], 2);
  map._sharedTooltip = L.tooltip({ sticky: true, className: 'custom-tooltip' });
  attachMapResetControl(map);
  attachIntentionalMapInteraction(map);
  return map;
}

function createUtilizationGeoJsonLayer(map, geoJSON, selectedCells, techLabel) {
  const layer = L.geoJSON(geoJSON, {
    style: (feature) => {
      const id = feature?.properties?.grid_cell;
      const info = selectedCells?.[id];
      return getCellStyle(info?.utilization_ratio, map.getZoom());
    },
    onEachFeature: (feature, l) => {
      const id = feature?.properties?.grid_cell;
      const info = selectedCells?.[id];
      const content = buildSimulatorMapCellTooltipHtml(info, { cellId: id, techLabel });
      l.on('mouseover', (e) => { map._sharedTooltip.setLatLng(e.latlng).setContent(content).addTo(map); });
      l.on('mousemove', (e) => { map._sharedTooltip.setLatLng(e.latlng); });
      l.on('mouseout', () => { map.closeTooltip(map._sharedTooltip); });
      l.on('click', () => {
        if (map._selectedFeature && map._selectedFeature !== l) {
          layer.resetStyle(map._selectedFeature);
        }
        l.setStyle({ color: '#000000', weight: 2, opacity: 1 });
        map._selectedFeature = l;
      });
    },
  });
  return layer;
}

function attachZoomStyleReset(map, layerRef) {
  const onZoom = () => {
    const lyr = layerRef.current;
    if (!lyr) return;
    lyr.eachLayer((l) => {
      if (l !== map._selectedFeature) lyr.resetStyle(l);
    });
  };
  map.on('zoomend', onZoom);
  return () => { map.off('zoomend', onZoom); };
}

function fitLayerBoundsAsHome(map, layer) {
  try {
    const bounds = layer.getBounds();
    if (bounds?.isValid?.()) {
      fitMapBoundsAsHome(map, bounds, { padding: [10, 10] });
    }
  } catch { /* empty layer */ }
}

/**
 * Leaflet simulator utilization maps: solar + combined wind (on/offshore layers).
 */
export function useSimulatorMaps({
  solarZonesGeoJSON,
  solarSelectedCells,
  windZonesGeoJSON,
  windSelectedCells,
  windOffZonesGeoJSON,
  windOffSelectedCells,
}) {
  const simulatorMapRef = useRef(null);
  const simulatorLayerRef = useRef(null);
  const simulatorWindMapRef = useRef(null);
  const simulatorWindLayerRef = useRef(null);
  const simulatorWindOffLayerRef = useRef(null);
  const windOnshoreLayerVisibleRef = useRef(true);
  const windOffshoreLayerVisibleRef = useRef(true);
  const [windMapLayerGeneration, setWindMapLayerGeneration] = useState(0);

  useEffect(() => {
    if (!simulatorMapRef.current && typeof window !== 'undefined') {
      simulatorMapRef.current = initSimulatorMap(SIMULATOR_MAP_ELEMENT_ID);
    }
  }, []);

  useEffect(() => {
    if (!simulatorWindMapRef.current && typeof window !== 'undefined') {
      simulatorWindMapRef.current = initSimulatorMap(SIMULATOR_WIND_MAP_ELEMENT_ID);
    }
  }, []);

  useEffect(() => {
    const map = simulatorMapRef.current;
    if (!map || !solarZonesGeoJSON) return;

    if (simulatorLayerRef.current) {
      map.removeLayer(simulatorLayerRef.current);
      simulatorLayerRef.current = null;
    }

    const layer = createUtilizationGeoJsonLayer(
      map,
      solarZonesGeoJSON,
      solarSelectedCells,
      'Solar'
    ).addTo(map);
    simulatorLayerRef.current = layer;
    fitLayerBoundsAsHome(map, layer);
    return attachZoomStyleReset(map, simulatorLayerRef);
  }, [solarZonesGeoJSON, solarSelectedCells]);

  useEffect(() => {
    const map = simulatorWindMapRef.current;
    if (!map || !windZonesGeoJSON) return;

    if (simulatorWindLayerRef.current) {
      map.removeLayer(simulatorWindLayerRef.current);
      simulatorWindLayerRef.current = null;
    }

    const layer = createUtilizationGeoJsonLayer(
      map,
      windZonesGeoJSON,
      windSelectedCells,
      'Onshore Wind'
    );
    simulatorWindLayerRef.current = layer;
    if (windOnshoreLayerVisibleRef.current) layer.addTo(map);
    setWindMapLayerGeneration((g) => g + 1);
    fitLayerBoundsAsHome(map, layer);
    return attachZoomStyleReset(map, simulatorWindLayerRef);
  }, [windZonesGeoJSON, windSelectedCells]);

  useEffect(() => {
    const map = simulatorWindMapRef.current;
    if (!map || !windOffZonesGeoJSON) return;

    if (simulatorWindOffLayerRef.current) {
      map.removeLayer(simulatorWindOffLayerRef.current);
      simulatorWindOffLayerRef.current = null;
    }

    const layer = createUtilizationGeoJsonLayer(
      map,
      windOffZonesGeoJSON,
      windOffSelectedCells,
      'Offshore Wind'
    );
    simulatorWindOffLayerRef.current = layer;
    if (windOffshoreLayerVisibleRef.current) layer.addTo(map);
    setWindMapLayerGeneration((g) => g + 1);
    return attachZoomStyleReset(map, simulatorWindOffLayerRef);
  }, [windOffZonesGeoJSON, windOffSelectedCells]);

  return {
    simulatorWindMapRef,
    simulatorWindLayerRef,
    simulatorWindOffLayerRef,
    windOnshoreLayerVisibleRef,
    windOffshoreLayerVisibleRef,
    windMapLayerGeneration,
  };
}
